import type { CfdiUse, FiscalRegime, InvoiceRequestInput } from "@cosmo/contracts";

export type FiscalDraft = {
  requested: boolean;
  rfc: string;
  legalName: string;
  fiscalPostalCode: string;
  fiscalRegime: FiscalRegime;
  cfdiUse: CfdiUse;
  email: string;
  observations: string;
};

export const emptyFiscalDraft: FiscalDraft = {
  requested: false, rfc: "", legalName: "", fiscalPostalCode: "",
  fiscalRegime: "601", cfdiUse: "G03", email: "", observations: "",
};

const regimes: Array<{ code: FiscalRegime; label: string }> = [
  { code: "601", label: "General de Ley Personas Morales" },
  { code: "603", label: "Personas Morales con Fines no Lucrativos" },
  { code: "605", label: "Sueldos y Salarios" },
  { code: "606", label: "Arrendamiento" },
  { code: "607", label: "Enajenación o Adquisición de Bienes" },
  { code: "608", label: "Demás ingresos" },
  { code: "610", label: "Residentes en el Extranjero" },
  { code: "611", label: "Ingresos por Dividendos" },
  { code: "612", label: "Personas Físicas con Actividades Empresariales" },
  { code: "614", label: "Ingresos por intereses" },
  { code: "615", label: "Ingresos por obtención de premios" },
  { code: "616", label: "Sin obligaciones fiscales" },
  { code: "620", label: "Sociedades Cooperativas de Producción" },
  { code: "621", label: "Incorporación Fiscal" },
  { code: "622", label: "Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras" },
  { code: "623", label: "Opcional para Grupos de Sociedades" },
  { code: "624", label: "Coordinados" },
  { code: "625", label: "Actividades Empresariales mediante Plataformas Tecnológicas" },
  { code: "626", label: "Régimen Simplificado de Confianza" },
];

const uses: Array<{ code: CfdiUse; label: string }> = [
  { code: "G01", label: "Adquisición de mercancías" },
  { code: "G02", label: "Devoluciones, descuentos o bonificaciones" },
  { code: "G03", label: "Gastos en general" },
  { code: "D01", label: "Honorarios médicos y gastos hospitalarios" },
  { code: "S01", label: "Sin efectos fiscales" },
  { code: "CP01", label: "Pagos" },
];

export function invoicePayload(value: FiscalDraft): InvoiceRequestInput {
  if (!value.requested) return { requested: false };
  return {
    requested: true, rfc: value.rfc.trim().toUpperCase(), legalName: value.legalName.trim(),
    fiscalPostalCode: value.fiscalPostalCode.trim(), fiscalRegime: value.fiscalRegime,
    cfdiUse: value.cfdiUse, email: value.email.trim(), observations: value.observations.trim() || null,
  };
}

export function FiscalRequestFields({ value, onChange, disabled = false, hideDecision = false }: {
  value: FiscalDraft; onChange: (value: FiscalDraft) => void; disabled?: boolean; hideDecision?: boolean;
}) {
  const set = <K extends keyof FiscalDraft>(key: K, next: FiscalDraft[K]) => onChange({ ...value, [key]: next });
  return <section className="sales-card fiscal-request-card">
    <div className="operation-panel-heading"><div><h2>Solicitud de factura</h2><p>Si el cliente requiere factura, captura sus datos fiscales antes de guardar la venta.</p></div>{!hideDecision && <label className="fiscal-toggle">¿Requiere factura?<select disabled={disabled} onChange={(event) => set("requested", event.target.value === "SI")} value={value.requested ? "SI" : "NO"}><option value="NO">No</option><option value="SI">Sí</option></select></label>}</div>
    {value.requested && <><div className="field-grid fiscal-fields">
      <label>RFC *<input autoCapitalize="characters" disabled={disabled} maxLength={13} minLength={12} onChange={(event) => set("rfc", event.target.value.toUpperCase().replace(/\s/g, ""))} pattern="[A-Z&Ñ]{3,4}[0-9]{6}[A-Z0-9]{3}" required value={value.rfc} /></label>
      <label>Nombre o razón social *<input disabled={disabled} maxLength={300} minLength={3} onChange={(event) => set("legalName", event.target.value)} required value={value.legalName} /></label>
      <label>Código postal fiscal *<input disabled={disabled} inputMode="numeric" maxLength={5} minLength={5} onChange={(event) => set("fiscalPostalCode", event.target.value.replace(/\D/g, ""))} pattern="\d{5}" required value={value.fiscalPostalCode} /></label>
      <label>Régimen fiscal *<select disabled={disabled} onChange={(event) => set("fiscalRegime", event.target.value as FiscalRegime)} value={value.fiscalRegime}>{regimes.map((item) => <option key={item.code} value={item.code}>{item.code} · {item.label}</option>)}</select></label>
      <label>Uso del CFDI *<select disabled={disabled} onChange={(event) => set("cfdiUse", event.target.value as CfdiUse)} value={value.cfdiUse}>{uses.map((item) => <option key={item.code} value={item.code}>{item.code} · {item.label}</option>)}</select></label>
      <label>Correo de entrega *<input disabled={disabled} maxLength={254} onChange={(event) => set("email", event.target.value)} required type="email" value={value.email} /></label>
    </div><label className="fiscal-observations">Observaciones opcionales<textarea disabled={disabled} maxLength={1000} onChange={(event) => set("observations", event.target.value)} value={value.observations} /></label><p className="lens-notice">La solicitud queda vinculada a la venta. El CFDI sólo podrá cargarse cuando el saldo esté liquidado.</p></>}
  </section>;
}
